import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDropdownStyles } from "components/FDFilterDropdown/FDFilterDropdownStyles";
import {
  EuiFieldSearch,
  EuiFilterButton,
  EuiPopoverTitle,
  useGeneratedHtmlId,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiText,
  EuiAccordion,
  EuiNotificationBadge,
} from "@elastic/eui";

import { CustomCheckbox } from "components/CustomInputs/CustomCheckbox";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import FDFilterDropdownFooter from "components/FDFilterDropdown/components/FDFilterDropdownFooter";
import Popover from "components/Popover/Popover";
import { isNullOrUndefined } from "utils";
import "styles/components/filterDropdown/fd_filter_dropdown.css";

const AccordionItem = ({
  option,
  activeFilters,
  filterItemClick,
  classes,
  setActiveFilters,
  onToggle,
  isOpen,
  filterText,
  singleTopLevelSelection,
}: any) => {
  const processedTagOptions = useMemo(() => {
    return option?.values?.length > 0
      ? option?.values?.map((i: any) => {
          const fullTagName = `${option.name}: ${i?.value}`;
          return {
            ...i,
            name: i?.value,
            fullTagName,
            checked: activeFilters[fullTagName] === "on" ? "on" : undefined,
            key: option?.name,
          };
        })
      : [
          {
            ...option,
            name: option?.name,
            fullTagName: option?.name,
            checked: activeFilters[option?.name] === "on" ? "on" : undefined,
            key: option?.name,
          },
        ];
  }, [option, activeFilters]);

  const filteredTagOptions = useMemo(() => {
    let options = processedTagOptions;

    if (filterText) {
      options = processedTagOptions.filter((tagOption: any) => {
        const tagValue = tagOption.name || "";
        const tagDisplay = tagOption.display || tagValue;

        return (
          tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
          tagDisplay.toLowerCase().includes(filterText.toLowerCase())
        );
      });
    }

    return options;
  }, [processedTagOptions, filterText]);

  const checkedCount = useMemo(() => {
    return processedTagOptions.filter(
      (tagOption: any) => activeFilters[tagOption.fullTagName] === "on"
    ).length;
  }, [processedTagOptions, activeFilters]);

  const areAllSelected = useMemo(() => {
    return processedTagOptions.length > 0 && checkedCount === processedTagOptions.length;
  }, [processedTagOptions.length, checkedCount]);

  const handleCheckboxClick = (e: any) => {
    e.stopPropagation();
    const newActiveFilters = { ...(!singleTopLevelSelection && activeFilters) };

    if (areAllSelected) {
      processedTagOptions.forEach((tagOption: any) => {
        newActiveFilters[tagOption.fullTagName] = undefined;
      });
    } else {
      processedTagOptions.forEach((tagOption: any) => {
        newActiveFilters[tagOption.fullTagName] = "on";
      });
    }

    setActiveFilters(newActiveFilters);
  };

  const handleTagOptionClick = (option: any) => {
    const parsedSelected = {
      name: option.fullTagName,
      value: option.fullTagName,
      label: option.fullTagName,
      display: option.fullTagName,
      key: option.key,
    };
    filterItemClick(parsedSelected);
  };

  const renderTagOption = (tagOption: any) => {
    return (
      <div
        key={tagOption.fullTagName}
        className={classes.tagOptionRow}
        onClick={() => handleTagOptionClick(tagOption)}
        style={{ cursor: "pointer" }}>
        <EuiFlexGroup
          style={{
            padding: "0 4px",
          }}
          alignItems="center"
          gutterSize="s">
          <CustomCheckbox
            onClick={() => {}}
            isChecked={activeFilters[tagOption.fullTagName] === "on"}
            isExcluded={activeFilters[tagOption.fullTagName] === "off"}
          />
          <EuiFlexItem>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: tagOption?.name?.length > 50 ? "11px" : "14px",
              }}>
              {tagOption?.type && <b>{tagOption?.type}:&nbsp;</b>}
              {tagOption.display ?? tagOption.name}
            </span>
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
    );
  };

  return (
    <div className={classes.tagAccordionButton}>
      <EuiAccordion
        id={`accordion-${option.name}`}
        buttonContent={
          <EuiFlexGroup
            style={{
              height: "33px",
            }}
            alignItems="center"
            gutterSize="s">
            <CustomCheckbox
              onClick={handleCheckboxClick}
              isChecked={areAllSelected}
              isExcluded={0}
              isPartial={checkedCount > 0}
            />
            <EuiFlexItem>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: option?.name?.length > 50 ? "11px" : "14px",
                }}>
                {option.display ?? option.name}
              </span>
            </EuiFlexItem>
            {filteredTagOptions?.length > 1 && checkedCount > 0 && (
              <EuiFlexItem grow={false}>
                <EuiNotificationBadge>{checkedCount}</EuiNotificationBadge>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        }
        paddingSize="s"
        buttonProps={{
          style: {
            width: "100%",
            display: "block",
          },
        }}
        forceState={isOpen ? "open" : "closed"}
        onToggle={(isOpen) => {
          if (onToggle) {
            option?.values?.length > 0 ? onToggle(option.name, isOpen) : "";
          }
        }}
        extraAction={null}
        arrowDisplay={option?.values?.length > 0 ? "right" : "none"}>
        <div style={{ marginTop: "0" }}>{filteredTagOptions.map(renderTagOption)}</div>
      </EuiAccordion>
    </div>
  );
};

const CustomSelector = ({
  Items = [],
  Name = "",
  Field = "",
  AllowExclude = false,
  popoverButtonProps = {},
  filterObject,
  setFilterObject,
  singleTopLevelSelection = true,
  isLoading = false,
  disableSorting = false,
  anchorPosition = "upCenter",
  width = null,
}: any) => {
  const classes = useDropdownStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [items, setItems] = useState<any>([]);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [filterText, setFilterText] = useState<string>("");
  const [stableOrder, setStableOrder] = useState([]);
  const [openAccordions, setOpenAccordions] = useState<any>({});

  const { clientId } = useSelector(globalFilterSelector);

  const publishGlobalFilter = useCallback(
    async (values: any) => {
      const newFieldGlobalFilterValues = Object.entries(values)
        .filter(([_, value]) => value !== undefined)
        .map(([listItem, _]) => listItem);

      setActiveFilters({});
      setFilterObject(newFieldGlobalFilterValues.length > 0 ? newFieldGlobalFilterValues : []);
    },
    [Field, clientId, AllowExclude]
  );

  useEffect(() => {
    if (Items) {
      const cleanItems = Items.filter((option: any) => !isNullOrUndefined(option.value));

      const mappedItems = cleanItems.map((f: any) => ({
        ...f,
        name: f.value,
      }));

      setItems(mappedItems);
    }
  }, [Items]);

  useEffect(() => {
    const map: any = {};
    if (filterObject) {
      for (const filterVal of filterObject) {
        map[filterVal] = "on";
      }

      setActiveFilters(map);
    } else {
      resetSelected();
    }
  }, [filterObject, JSON.stringify(items)]);

  const togglePopover = () => {
    const newIsOpen = !isPopoverOpen;
    if (newIsOpen) {
      recalculateOrder();
    }
    setIsPopoverOpen(newIsOpen);
  };

  const resetSelected = useCallback(() => {
    if (Object.keys(filterObject).length === 0) setActiveFilters({});
  }, [Name, JSON.stringify(activeFilters), JSON.stringify(items), filterObject]);

  useEffect(
    () => () => {
      setItems([]);
      setLoading(false);
    },
    []
  );

  const activeFiltersCount = Object.keys(activeFilters).length;

  const filterArrowDownButton = (
    <EuiFilterButton
      isLoading={isLoading}
      className={activeFiltersCount == 0 ? "euiFilterButton__notification_hidden" : ""}
      data-test-subj={`filter-dd-${Name}`}
      hasActiveFilters={activeFiltersCount > 0}
      iconType="arrowDown"
      iconSize="s"
      isSelected={isPopoverOpen}
      numActiveFilters={activeFiltersCount}
      numFilters={items.length}
      onClick={togglePopover}
      style={{
        background: "var(--card-background3)",
        border: "1px solid var(--border-color)",
        borderRadius: "10px",
        boxShadow: "none",
        height: "40px",
        marginRight: width ? "0px" : "10px",
        position: "relative",
        width: width || "590px",
        fontWeight: "500",
      }}
      {...popoverButtonProps}>
      <span>{Name}</span>
    </EuiFilterButton>
  );

  const filterItemClick = (item: any) => {
    const currentChecked = item ? activeFilters[item.name] : undefined;
    let newChecked;

    newChecked = currentChecked === "on" ? undefined : "on";

    const filterListStatusCopy = { ...activeFilters };

    for (const key in filterListStatusCopy) {
      if (!key.startsWith(item.key)) {
        delete filterListStatusCopy[key];
      }
    }

    if (newChecked === undefined) {
      delete filterListStatusCopy[item.name];
    } else {
      filterListStatusCopy[item.name] = newChecked;
    }
    setActiveFilters(filterListStatusCopy);
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
    setOpenAccordions({});
  };

  const applyFilters = () => {
    setFilterText("");
    publishGlobalFilter(activeFilters);
    closePopover();
    recalculateOrder();
  };

  const cancelFiltersSelection = () => {
    setFilterText("");
    const map: any = {};
    if (filterObject) {
      for (const filterVal of filterObject) {
        map[filterVal] = "on";
      }

      setActiveFilters(map);
    }
    closePopover();
  };

  const serachId = useGeneratedHtmlId();

  const dropdownOptions: any[] = items
    .filter((item: any) => {
      const accordionHeaderMatches =
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase());

      if (accordionHeaderMatches) return true;

      if (item.values && item.values.length > 0) {
        return item.values.some((tagOption: any) => {
          const tagValue = tagOption.value || "";
          const tagDisplay = tagOption.display || tagValue;

          return (
            tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
            tagDisplay.toLowerCase().includes(filterText.toLowerCase())
          );
        });
      }

      return false;
    })
    .sort((a: any, b: any) => a.name - b.name)
    .map((i: any) => ({
      ...i,
      label: i.name,
    }));

  const getCheckedCountForOption = (option: any) => {
    return singleTopLevelSelection
      ? option.values?.reduce((count: any, item: any) => {
          const fullTagName = `${option.name}: ${item?.value}`;
          return activeFilters[fullTagName] === "on" || activeFilters[fullTagName] === "off"
            ? count + 1
            : count;
        }, 0) || 0
      : activeFilters[option.name] === "on"
        ? 1
        : 0;
  };

  const recalculateOrder = useCallback(() => {
    const filteredItems = items.filter((item: any) => {
      return (
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase())
      );
    });

    const newOrder = [...filteredItems]?.sort((a, b) => {
      const aCheckedCount = getCheckedCountForOption(a);
      const bCheckedCount = getCheckedCountForOption(b);

      if (aCheckedCount > 0 && bCheckedCount === 0) return -1;
      if (aCheckedCount === 0 && bCheckedCount > 0) return 1;

      if (aCheckedCount > 0 && bCheckedCount > 0) {
        return bCheckedCount - aCheckedCount;
      }

      const aText = a.display || a.name;
      const bText = b.display || b.name;
      return aText.localeCompare(bText);
    });

    //@ts-ignore
    setStableOrder(newOrder.map((item) => item.name));
  }, [items, filterText, activeFilters]);

  useEffect(() => {
    if (items.length > 0) {
      recalculateOrder();
    }
  }, [items]);

  const handleAccordionToggle = (optionName: any, isOpen: boolean) => {
    if (isOpen) {
      setOpenAccordions({ [optionName]: true });
    } else {
      setOpenAccordions((prev: any) => {
        const newOpenAccordions: any = { ...prev };
        delete newOpenAccordions[optionName];
        return newOpenAccordions;
      });
    }
  };

  const getSortedDropdownOptions = () => {
    const filteredObj = filterObject?.map((i: any) => i?.split(":")?.[1]?.trim());
    if (disableSorting) return dropdownOptions;
    if (stableOrder.length === 0) {
      return dropdownOptions?.map((i: any) => ({
        ...i,
        values: [
          ...i?.values?.filter((i: any) => filteredObj?.includes(i.name)),
          ...i?.values?.filter((i: any) => !filteredObj?.includes(i.name)),
        ],
        label: i.name,
      }));
    }

    const itemMap: any = {};
    dropdownOptions.forEach((item) => {
      itemMap[item.name] = item;
    });

    return stableOrder
      .filter((name) => itemMap[name])
      .map((name) => itemMap[name])
      ?.map((i: any) => ({
        ...i,
        values: [
          ...i?.values?.filter((i: any) => filteredObj?.includes(i.name)),
          ...i?.values?.filter((i: any) => !filteredObj?.includes(i.name)),
        ],
        label: i.name,
      }));
  };

  useEffect(() => {
    if (filterText.length > 0) {
      const newOpenAccordions: any = {};

      items.forEach((item: any) => {
        const headerMatches =
          (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
          (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase());

        if (item.values && item.values.length > 0) {
          const hasMatchingChildren = item.values.some((tagOption: any) => {
            const tagValue = tagOption.value || "";
            const tagDisplay = tagOption.display || tagValue;

            return (
              tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
              tagDisplay.toLowerCase().includes(filterText.toLowerCase())
            );
          });

          if (headerMatches || hasMatchingChildren) {
            newOpenAccordions[item.name] = true;
          }
        } else if (headerMatches) {
          newOpenAccordions[item.name] = true;
        }
      });

      setOpenAccordions(newOpenAccordions);
    } else if (filterText.length === 0) {
      setOpenAccordions({});
    }
  }, [filterText, items]);

  return (
    <>
      {
        <Popover
          noCloseOnScroll
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              cancelFiltersSelection();
            }
          }}
          anchorPosition={anchorPosition}
          button={filterArrowDownButton}
          onClose={cancelFiltersSelection}
          isOpen={isPopoverOpen}
          ownFocus
          children={
            <>
              <EuiPopoverTitle className={classes.filterPopoverTitle}>
                <EuiFieldSearch
                  className={`${classes.search_input} filter-search-input`}
                  id={serachId}
                  isClearable
                  onChange={(e) => {
                    setFilterText(e.target.value);
                  }}
                  style={{
                    borderTopLeftRadius: 6,
                    borderTopRightRadius: 6,
                    borderBottom: ".6px solid var(--border-color)",
                  }}
                  value={filterText}
                />
              </EuiPopoverTitle>
              <div
                className="euiFilterSelect__items"
                style={{
                  overflowY: "auto",
                  maxHeight: "400px",
                  scrollbarWidth: "thin",
                  minWidth: "400px",
                }}>
                {loading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                    <EuiFlexGroup
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                      gutterSize="s">
                      <EuiFlexItem grow={false}>
                        <EuiLoadingSpinner size="s" />
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiText>
                          <p style={{ fontSize: "var(--fs12)", color: "#516381" }}>Loading data</p>
                        </EuiText>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </div>
                ) : getSortedDropdownOptions().length > 0 ? (
                  getSortedDropdownOptions().map((option) => (
                    <AccordionItem
                      key={option.name || option.label}
                      option={option}
                      activeFilters={activeFilters}
                      filterItemClick={filterItemClick}
                      classes={classes}
                      setActiveFilters={setActiveFilters}
                      onToggle={handleAccordionToggle}
                      isOpen={!!openAccordions[option.name]}
                      filterText={filterText}
                      singleTopLevelSelection={singleTopLevelSelection}
                    />
                  ))
                ) : (
                  <div style={{ padding: "16px", textAlign: "center" }}>No data found</div>
                )}
              </div>
              <FDFilterDropdownFooter onApply={applyFilters} onCancel={cancelFiltersSelection} />
            </>
          }
        />
      }
    </>
  );
};

export default CustomSelector;
